import { useState } from 'react'
import type { CustomerDTO } from '@wf/shared'
import { ApiError } from '../api/client'
import { newUuid } from '../offline/uuid'
import { useCreateCustomer, useCustomersList, usePatchCustomer } from './useCustomers'

interface Fields {
  name: string
  phone: string
  email: string
  address: string
}
const EMPTY: Fields = { name: '', phone: '', email: '', address: '' }
const fromCustomer = (c: CustomerDTO): Fields => ({
  name: c.name,
  phone: c.phone ?? '',
  email: c.email ?? '',
  address: c.address ?? '',
})
const norm = (s: string | null | undefined): string => (s ?? '').trim()

/**
 * Kundenauswahl im Neuen Auftrag — ersetzt das Dropdown durch ein freies Namensfeld mit Live-Vorschlägen:
 * Tippen filtert die Kundenliste (clientseitig, gedeckelt); „Übernehmen" wählt einen Bestandskunden und
 * füllt Telefon/E-Mail/Anschrift zum Prüfen. Kein Treffer → Felder ausfüllen und per „Kunde anlegen" neu
 * anlegen (idempotent über client_uuid). Bei einem übernommenen Kunden erscheint „Daten aktualisieren" nur,
 * wenn etwas geändert wurde, und schreibt per Optimistic Concurrency (version) zurück.
 * Die Komponente ist Source-of-Truth der Auswahl und meldet die customerId über onChange nach oben.
 */
export function CustomerPicker({ onChange }: { onChange: (customerId: number | null) => void }) {
  const customers = useCustomersList()
  const createCustomer = useCreateCustomer()
  const [selected, setSelected] = useState<CustomerDTO | null>(null)
  const [fields, setFields] = useState<Fields>(EMPTY)
  const patchCustomer = usePatchCustomer(selected?.id ?? 0)

  const set = (key: keyof Fields) => (e: { target: { value: string } }) =>
    setFields((prev) => ({ ...prev, [key]: e.target.value }))

  const query = fields.name.trim().toLowerCase()
  // Vorschläge nur im Such-/Neu-Modus (kein Kunde übernommen) und sobald etwas getippt wurde.
  const matches =
    selected == null && query
      ? (customers.data?.customers ?? []).filter((c) => c.name.toLowerCase().includes(query)).slice(0, 8)
      : []

  const dirty =
    selected != null &&
    (norm(fields.name) !== norm(selected.name) ||
      norm(fields.phone) !== norm(selected.phone) ||
      norm(fields.email) !== norm(selected.email) ||
      norm(fields.address) !== norm(selected.address))

  function pick(c: CustomerDTO): void {
    setSelected(c)
    setFields(fromCustomer(c))
    onChange(c.id)
  }
  function clear(): void {
    setSelected(null)
    setFields(EMPTY)
    onChange(null)
  }
  function createNew(): void {
    if (!fields.name.trim()) return
    createCustomer.mutate(
      {
        name: fields.name.trim(),
        phone: fields.phone.trim() || null,
        email: fields.email.trim() || null,
        address: fields.address.trim() || null,
        client_uuid: newUuid(),
      },
      { onSuccess: (res) => pick(res.customer) },
    )
  }
  function updateExisting(): void {
    if (!selected) return
    patchCustomer.mutate(
      {
        version: selected.version,
        name: fields.name.trim(),
        phone: fields.phone.trim() || null,
        email: fields.email.trim() || null,
        address: fields.address.trim() || null,
      },
      { onSuccess: (res) => setSelected(res.customer) },
    )
  }

  const error =
    createCustomer.error instanceof ApiError
      ? createCustomer.error.message
      : patchCustomer.error instanceof ApiError
        ? patchCustomer.error.message
        : null

  return (
    <div className="wf-field">
      <div className="wf-labelrow">
        <span className="wf-label">Kunde</span>
        {selected != null ? (
          <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={clear}>
            ✕ Anderen Kunden
          </button>
        ) : null}
      </div>
      <input
        className="wf-input"
        aria-label="Name"
        autoComplete="off"
        placeholder="Name eingeben oder Bestandskunden suchen …"
        value={fields.name}
        onChange={set('name')}
      />

      {matches.length > 0 ? (
        <div className="wf-linelist">
          {matches.map((c) => (
            <div key={c.id} className="wf-line">
              <span className="wf-line__main">
                {c.name}
                {c.phone ? ` · ${c.phone}` : ''}
              </span>
              <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => pick(c)}>
                Übernehmen
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Telefon</span>
          <input className="wf-input" type="tel" aria-label="Telefon" value={fields.phone} onChange={set('phone')} placeholder="0123 456789" />
        </label>
        <label className="wf-field">
          <span className="wf-label">E-Mail</span>
          <input className="wf-input" type="email" aria-label="E-Mail" value={fields.email} onChange={set('email')} placeholder="name@mail.de" />
        </label>
      </div>
      <label className="wf-field">
        <span className="wf-label">Anschrift</span>
        <input className="wf-input" aria-label="Anschrift" value={fields.address} onChange={set('address')} placeholder="Musterstr. 1, 12345 Stadt" />
      </label>

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {selected == null ? (
        <button type="button" className="wf-btn wf-btn--ghost" onClick={createNew} disabled={!fields.name.trim() || createCustomer.isPending}>
          {createCustomer.isPending ? 'Anlegen…' : '＋ Kunde anlegen'}
        </button>
      ) : dirty ? (
        <button type="button" className="wf-btn wf-btn--ghost" onClick={updateExisting} disabled={!fields.name.trim() || patchCustomer.isPending}>
          {patchCustomer.isPending ? 'Speichern…' : 'Daten aktualisieren'}
        </button>
      ) : (
        <p className="sub" aria-live="polite">
          ✓ Bestandskunde übernommen
        </p>
      )}
    </div>
  )
}
